import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, styled, useMediaQuery } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import LazyImage from "../LazyLoadingImage/LazyImage";
import { collectItemImages } from "./itemImages";
import { resolveThumbUrl } from "../../utils/assets";
import { PRODUCT_DETAIL_TEXTS } from "../../pages/shared/constants";

/** How far a finger has to travel before it counts as a swipe rather than a tap. */
const SWIPE_THRESHOLD_PX = 40;

/** Matches `Banner.tsx`, so the two carousels on the site move at the same pace. */
const AUTOPLAY_INTERVAL_MS = 5000;

const Viewport = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",
  overflow: "hidden",
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.background.default,
  // The image is the touch target for swiping; without this a drag selects it instead.
  touchAction: "pan-y",
}));

const Slide = styled(Box)({
  width: "100%",
  height: "100%",
  "@keyframes itemImageFadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: "itemImageFadeIn 200ms ease",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

const NavButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  "&:hover": {
    backgroundColor: theme.palette.background.paper,
  },
}));

const Dots = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(1),
}));

interface DotProps {
  $active: boolean;
}
const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$active",
})<DotProps>(({ theme, $active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: $active ? theme.palette.text.primary : theme.palette.divider,
}));

const Thumbs = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

interface ThumbProps {
  $active: boolean;
}
const Thumb = styled("button", {
  shouldForwardProp: (prop) => prop !== "$active",
})<ThumbProps>(({ theme, $active }) => ({
  padding: 0,
  cursor: "pointer",
  flex: "0 0 auto",
  width: 56,
  height: 56,
  borderRadius: theme.spacing(0.5),
  overflow: "hidden",
  background: "none",
  border: `2px solid ${$active ? theme.palette.text.primary : theme.palette.divider}`,
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

const SrOnly = styled("span")({
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
});

type ImageCarouselProps = {
  item: { picture?: string | null; images?: string[]; name: string };
  /**
   * The thumbnail strip is desktop-only: on a phone it competes for width with the image it is
   * navigating, where dots plus swiping already do the job.
   */
  showThumbnails?: boolean;
};

/**
 * The product gallery (EB-537).
 *
 * Hand-rolled rather than pulled from a library, following `Banner.tsx` — including, since EB-554,
 * its autoplay. The original built this without one on the reasoning that a gallery advancing on
 * its own fights the person looking at a photo; that reasoning survives as the *pause* rules rather
 * than as a missing feature. Left alone the carousel is the only thing that says a second photo
 * exists, since the dots read as decoration; the moment attention is on it — a pointer over it,
 * focus inside it, or any manual navigation — it stops and stays stopped for that product.
 *
 * Only the current image is mounted. The alternative, a sliding track holding every slide, looks
 * smoother but defeats `LazyImage`: off-screen slides never intersect the viewport, so they stay
 * unloaded and the slide animates to a blank frame.
 */
const ImageCarousel: React.FC<ImageCarouselProps> = ({ item, showThumbnails = false }) => {
  const images = useMemo(() => collectItemImages(item), [item]);
  const [index, setIndex] = useState(0);

  // The two ways autoplay yields, in refs rather than in state: neither changes what is rendered,
  // only whether the next tick is allowed to act, and re-rendering the whole gallery on a mouseenter
  // would be pure waste. `paused` lifts when the pointer or the focus leaves; `stopped` does not —
  // someone comparing two photos should not be handed the third five seconds later, and re-arming
  // on a timer would only reproduce that.
  const paused = React.useRef(false);
  const stopped = React.useRef(false);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });

  const count = images.length;
  const first = images[0]?.url;

  // Navigating between products reuses this component, and an index left pointing at the fifth
  // photo of the previous product would render nothing. A new product also gets its autoplay back:
  // the decision not to be advanced was about the photos just left behind.
  useEffect(() => {
    setIndex(0);
    stopped.current = false;
  }, [first, count]);

  const go = useCallback(
    (delta: number) => {
      stopped.current = true;
      setIndex((current) => (current + delta + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count < 2 || prefersReducedMotion) return;

    const timer = setInterval(() => {
      if (paused.current || stopped.current) return;
      setIndex((current) => (current + 1) % count);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [count, prefersReducedMotion]);

  const touchStartX = React.useRef<number | null>(null);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null) return;

    const travelled = (event.changedTouches[0]?.clientX ?? start) - start;
    if (Math.abs(travelled) < SWIPE_THRESHOLD_PX) return;

    go(travelled < 0 ? 1 : -1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      go(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      go(1);
    }
  };

  if (count === 0) return null;

  const current = images[Math.min(index, count - 1)];
  const hasControls = count > 1;

  return (
    <Box
      role="group"
      aria-roledescription={PRODUCT_DETAIL_TEXTS.carouselRoleDescription}
      aria-label={item.name}
      onKeyDown={hasControls ? handleKeyDown : undefined}
      // React's focus events bubble, so these cover the arrows and the thumbnail strip too — which
      // is the point: a keyboard user who has tabbed in here is looking at this, not at a slideshow.
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onFocus={() => (paused.current = true)}
      onBlur={() => (paused.current = false)}
    >
      <Viewport onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* Keyed so a change of photo restarts the fade rather than swapping mid-animation. */}
        <Slide key={current.url}>
          <LazyImage src={current.url} alt={`${item.name} ${index + 1}`} />
        </Slide>

        {hasControls && (
          <>
            <NavButton
              size="small"
              sx={{ left: 8 }}
              aria-label={PRODUCT_DETAIL_TEXTS.carouselPrevious}
              onClick={() => go(-1)}
            >
              <ChevronLeft />
            </NavButton>
            <NavButton
              size="small"
              sx={{ right: 8 }}
              aria-label={PRODUCT_DETAIL_TEXTS.carouselNext}
              onClick={() => go(1)}
            >
              <ChevronRight />
            </NavButton>
          </>
        )}
      </Viewport>

      {hasControls && (
        <>
          <SrOnly aria-live="polite">
            {PRODUCT_DETAIL_TEXTS.carouselPosition(index + 1, count)}
          </SrOnly>

          <Dots>
            {images.map((image, i) => (
              <Dot key={image.url} $active={i === index} />
            ))}
          </Dots>

          {showThumbnails && (
            <Thumbs>
              {images.map((image, i) => (
                <Thumb
                  key={image.url}
                  type="button"
                  $active={i === index}
                  aria-label={PRODUCT_DETAIL_TEXTS.carouselGoTo(i + 1)}
                  aria-current={i === index}
                  onClick={() => {
                    stopped.current = true;
                    setIndex(i);
                  }}
                >
                  {/* A thumbnail is a nicety: if EB-518 has not generated one for this key,
                      `resolveThumbUrl` returns "" and the full image stands in. */}
                  <LazyImage src={resolveThumbUrl(image.key) || image.url} alt="" />
                </Thumb>
              ))}
            </Thumbs>
          )}
        </>
      )}
    </Box>
  );
};

export default ImageCarousel;
